/**
 * Los traspasos de proyecto que están esperando respuesta, en el panel.
 *
 * Hermano de `TeamInviteBanner` y por el mismo motivo: el correo no es un canal
 * fiable —en local no hay clave de Resend y no sale ninguno, y en producción
 * está el spam— así que sin esto un traspaso vive ENTERO dentro de un enlace que
 * quizá nadie llegue a ver. Quien entrega se queda esperando una respuesta que
 * no llega porque el aviso nunca apareció.
 *
 * ── Por qué aquí NO se acepta ───────────────────────────────────────────────
 *
 * A diferencia de una invitación al equipo, esto lleva a una pantalla y no
 * resuelve en el sitio. Recibir un proyecto es hacerse cargo de un sitio
 * publicado, de un dominio y de una base de datos personales de terceros, y
 * además el plan pasa a ser cosa tuya. Todo eso hay que verlo antes de decidir,
 * y no cabe en una banda: para eso existe `TransferAccept`, que lo enumera.
 */
import { useCallback, useEffect, useState } from 'react'
import { IconArrowRight, IconBuildingStore } from '@tabler/icons-react'
import { listMyTransfers, type MyTransfer } from '../lib/api'
import { transferReviewHref } from '../lib/transferInvite'
import { reportPending } from '../lib/pendingNotices'
import { MascotOutlinePattern } from './MascotOutlinePattern'
import { useI18n } from '../lib/i18n'

export function TransferBanner({ token }: { token: string }) {
  const { t } = useI18n()
  const [transfers, setTransfers] = useState<MyTransfer[]>([])

  const load = useCallback(async () => {
    try {
      setTransfers(await listMyTransfers(token))
    } catch {
      /* En silencio: es un añadido al panel, no el panel. Que falle su consulta
         no puede llenar de errores la pantalla de alguien que probablemente no
         tiene ningún traspaso esperando. */
    }
  }, [token])

  useEffect(() => { void load() }, [load])

  /* Igual que sus hermanos: la mascota de abajo mira hacia aquí. */
  useEffect(() => {
    reportPending('transfer', transfers.length)
    return () => { reportPending('transfer', 0) }
  }, [transfers.length])

  if (transfers.length === 0) return null

  return (
    <div className="dash-invite-banners">
      {transfers.map((transfer) => {
        const days = Math.max(
          0,
          Math.ceil((new Date(transfer.expiresAt).getTime() - Date.now()) / 86_400_000),
        )
        return (
          <section className="dash-invite-banner dash-loaded-content-in" key={transfer.id}>
            {/* Las mismas clases que el banner de invitación, y el mismo dibujo:
                son el mismo tipo de aviso —algo que alguien te ofrece y espera
                respuesta— y verlos distintos haría pensar que uno de los dos es
                otra cosa. Sin ojos por lo mismo que allí: el botón cae justo
                encima y las dos únicas formas rellenas se transparentaban. */}
            <MascotOutlinePattern className="dash-invite-banner-pattern" eyes={false} />

            <div className="dash-invite-banner-copy">
              <span className="dash-invite-banner-kicker">
                <IconBuildingStore size={13} aria-hidden="true" />
                {t('transferBanner.kicker')}
              </span>
              <strong>
                {transfer.fromName
                  ? <>{t('transferBanner.fromWho', { name: transfer.fromName })} <em>{transfer.projectName}</em></>
                  : <>{t('transferBanner.anon')} <em>{transfer.projectName}</em></>}
              </strong>
              <p>{t('transferBanner.body')}</p>
            </div>

            <div className="dash-invite-banner-actions">
              <span className="dash-invite-banner-expiry">
                {t(days === 1 ? 'transferBanner.expiresOne' : 'transferBanner.expiresMany', { count: days })}
              </span>
              {/* Un solo botón, y no «aceptar / rechazar» como en las
                  invitaciones: aquí las dos respuestas se dan DESPUÉS de ver el
                  inventario, no antes. Ofrecer «aceptar» en la banda sería
                  invitar a decidir sin haber leído lo que se asume. */}
              {/* Un enlace de verdad y no un botón con estado: la pantalla que
                  abre tiene dirección propia, así que esto puede abrirse en otra
                  pestaña, copiarse y sobrevivir a una recarga. */}
              <a className="dash-invite-banner-btn is-primary" href={transferReviewHref(transfer.id)}>
                {t('transferBanner.cta')} <IconArrowRight size={14} aria-hidden="true" />
              </a>
            </div>
          </section>
        )
      })}
    </div>
  )
}
